import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from '../../styles/components.js';

const TestimonialsSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.background};
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 650px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
`;

const Label = styled.div`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}0a;
  padding: 5px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.primary}18;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 1rem;
  letter-spacing: -0.03em;
  span {
    background: ${({ theme }) => theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Desc = styled.p`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.textGray};
  line-height: 1.7;
  margin-bottom: 0;
`;

const SliderWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
`;

const CardWrapper = styled.div`
  position: relative;
  min-height: 320px;
  display: flex;
  align-items: center;
`;

const TestimonialCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border-radius: 24px;
  box-shadow: ${({ theme }) => theme.shadows.large};
  padding: 3rem 3rem 2.5rem;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  width: 100%;
  overflow: hidden;

  &::before {
    content: '“';
    position: absolute;
    top: -10px;
    left: 30px;
    font-size: 10rem;
    line-height: 1;
    font-family: 'Space Grotesk', serif;
    font-weight: 900;
    color: ${({ theme }) => theme.colors.primary};
    opacity: 0.08;
    pointer-events: none;
  }
`;

const Stars = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 1.5rem;
  
  svg {
    width: 18px;
    height: 18px;
    fill: #f59e0b; /* Neon Amber Gold */
  }
`;

const TestimonialText = styled.p`
  font-size: 1.15rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textDark};
  line-height: 1.8;
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
  letter-spacing: -0.01em;
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
  box-shadow: ${({ theme }) => theme.shadows.glowSm};
`;

const AuthorInfo = styled.div`
  text-align: left;
`;

const AuthorName = styled.div`
  font-weight: 800;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textDark};
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.01em;
`;

const AuthorTitle = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 2.25rem;
`;

const NavBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceAlt};
  color: ${({ theme }) => theme.colors.textDark};
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}50;
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}0a;
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? '20px' : '8px')};
  height: 8px;
  border-radius: 99px;
  border: none;
  background: ${({ $active, theme }) => $active ? theme.colors.gradient : theme.colors.border};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: ${({ theme }) => theme.colors.gradient};
  border-radius: 2px;
  transition: width 0.1s linear;
  width: ${({ $progress }) => $progress}%;
`;

const testimonials = [
  {
    id: 1,
    stars: 5,
    text: "Tauseef built our SEVN CLOUDS app from scratch with an incredibly smooth audio player, subscription system, and beautiful UI. The app now has 4.9 stars on the App Store. Truly exceptional work!",
    name: "Sarah Johnson",
    title: "CEO, Tech Innovations",
    initials: "SJ"
  },
  {
    id: 2,
    stars: 5,
    text: "The attention to detail and creativity Tauseef brought to our project exceeded our expectations. Our app not only looks amazing but also integrates complex features seamlessly.",
    name: "Michael Thompson",
    title: "Marketing Director, Global Brands",
    initials: "MT"
  },
  {
    id: 3,
    stars: 5,
    text: "Working with Tauseef was outstanding. He architected a complete pet-service platform with real-time chat, Stripe payments, and map-based search — all delivered on time!",
    name: "Emily Rodriguez",
    title: "Founder, StartUp Solutions",
    initials: "ER"
  }
];

const StarIcon = () => (
  <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const INTERVAL = 4500;

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const startTimeRef = useRef(Date.now());

  const go = (i) => {
    setCurrent((i + testimonials.length) % testimonials.length);
    setProgress(0);
    startTimeRef.current = Date.now();
  };

  useEffect(() => {
    if (paused) return;
    const tick = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min((elapsed / INTERVAL) * 100, 100);
      setProgress(pct);
      if (elapsed >= INTERVAL) {
        setCurrent(c => (c + 1) % testimonials.length);
        setProgress(0);
        startTimeRef.current = Date.now();
      }
    }, 50);
    return () => clearInterval(tick);
  }, [paused, current]);

  return (
    <TestimonialsSection id="testimonials">
      <Container>
        <SectionHeader>
          <Label>05 / TESTIMONIALS</Label>
          <Title>Client <span>Success Stories</span></Title>
          <Desc>Don't just take my word for it — hear from clients who have shipped apps with my help.</Desc>
        </SectionHeader>

        <SliderWrapper
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <CardWrapper>
            <AnimatePresence mode="wait">
              <TestimonialCard
                key={current}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4 }}
              >
                <Stars>
                  {Array.from({ length: testimonials[current].stars }).map((_, i) => (
                    <StarIcon key={i} />
                  ))}
                </Stars>
                <TestimonialText>"{testimonials[current].text}"</TestimonialText>
                <AuthorRow>
                  <Avatar>{testimonials[current].initials}</Avatar>
                  <AuthorInfo>
                    <AuthorName>{testimonials[current].name}</AuthorName>
                    <AuthorTitle>{testimonials[current].title}</AuthorTitle>
                  </AuthorInfo>
                </AuthorRow>
                <ProgressBar $progress={progress} />
              </TestimonialCard>
            </AnimatePresence>
          </CardWrapper>

          <Controls>
            <NavBtn onClick={() => go(current - 1)} aria-label="Previous">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </NavBtn>
            <Dots>
              {testimonials.map((_, i) => (
                <Dot key={i} $active={i === current} onClick={() => go(i)} />
              ))}
            </Dots>
            <NavBtn onClick={() => go(current + 1)} aria-label="Next">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </NavBtn>
          </Controls>
        </SliderWrapper>
      </Container>
    </TestimonialsSection>
  );
};

export default Testimonials;